import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { isEmpty, isNil } from 'lodash';

// Components
import ClearHoldingsModal from '@/views/components/modals/portfolio-modals/ClearPortfolioHoldingsModal';
import Modal from '@/views/components/modals/AccessibleModal';
import MultiPortfoliosEditorFooterActions from './components/MultiPortfoliosEditorFooterActions/MultiPortfoliosEditorFooterActions';
import PortfolioEditorFooterActions from './components/PortfolioEditorFooterActions/PortfolioEditorFooterActions';
import { withErrorBoundary } from '@/views/components/errors';

// Redux
import { clearHoldings as clearHoldingsAction, setHoldingToReplace } from '@/redux/modules/edit-portfolio';
import {
  hasNextPortfolio,
  isInMultiPortfolioMode as getIsInMultiPortfolioMode,
  processNextPortfolio as processNextPortfolioAction,
  resetMultiPortfolio as resetMultiPortfolioAction,
} from '@/redux/modules/ai-statement-reader-multi-portfolio';
import { selectShelfStatusedPortfolioHoldings } from '@/redux/modules/shelf-management/selectors';

// Types
import ComparisonItem from '@/types/comparison-item';
import Portfolio from '@/types/portfolio';
import PortfolioHolding from '@/types/portfolio-holding';
import { ReduxState } from '@/redux/modules/types';
import Nullable from '@/types/nullable';
import User from '@/types/user';

// Utils
import { canAccessMultiPortfoliosStatementReader } from '@/utils/feature-access';
import { formatLocalizedString, formatMessage } from '@/utils/i18n';
import { getPortfolioMissingOrInvalidHoldings } from '@/utils/portfolio/utils';
import notification from '@/utils/notification';

type DispatchProps = {
  clearHoldings: () => void;
  processNextPortfolio: () => void;
  resetMultiPortfolio: () => void;
};

type StateProps = {
  comparisonItem?: ComparisonItem;
  isCalculating: boolean;
  isDirty: boolean;
  isInMultiPortfolioMode: boolean;
  isPortfolioValueLocked?: boolean;
  isSaving: boolean;
  missingHoldings: PortfolioHolding[];
  portfolio: Portfolio;
  showTaxDetails?: boolean;
  hasMorePortfolios: boolean;
  user: Nullable<User>;
  savedPortfolioCount: number; // Track how many saved
};

type Props = DispatchProps &
  StateProps & {
    allowUnallocated?: boolean;
    isComparisonReran: boolean;
    totalAllocated: number;
    onSave: () => Promise<void>;
    onCancel: (portfolio?: Portfolio) => void;
    showMissingCostBasisBanner?: boolean;
    setShowMissingCostBasisBanner: (show: boolean) => void;
    isMissingCostBasis?: boolean;
  };

const PortfolioEditorFooter = ({
  comparisonItem,
  clearHoldings,
  isCalculating,
  isComparisonReran,
  isDirty,
  isInMultiPortfolioMode,
  isPortfolioValueLocked,
  isSaving,
  missingHoldings,
  portfolio,
  totalAllocated,
  allowUnallocated,
  onSave,
  onCancel,
  processNextPortfolio,
  resetMultiPortfolio,
  setShowMissingCostBasisBanner,
  showMissingCostBasisBanner = false,
  isMissingCostBasis = false,
  showTaxDetails,
  hasMorePortfolios,
  user,
  savedPortfolioCount,
}: Props) => {
  const [showClearHoldingsModal, setShowClearHoldingsModal] = useState(false);
  const isMultiPortfoliosEnabled = canAccessMultiPortfoliosStatementReader(user) && isInMultiPortfolioMode;

  useEffect(() => {
    if (showMissingCostBasisBanner && !isMissingCostBasis) {
      setShowMissingCostBasisBanner(false);
    }
  }, [showMissingCostBasisBanner, isMissingCostBasis, setShowMissingCostBasisBanner]);

  const isSaveDisabled = (): boolean => {
    // Boolean variables representing anomalies in a Portfolio or its Holdings
    const isHoldingsMissingDollars = portfolio.holdings.some(holding => !holding.dollars);
    const isMissingHoldings = missingHoldings.length > 0;
    const isNameMissing = !formatLocalizedString(portfolio.name);
    const isSizeInvalid = !portfolio.size || portfolio.size <= 0;
    const isNotFullyAllocated = totalAllocated !== 100;
    const allocationWithinRange = totalAllocated > 0 && totalAllocated <= 100;
    const shouldDisableForCostBasis = showMissingCostBasisBanner && isMissingCostBasis;
    const hasInvalidTaxLots =
      !!showTaxDetails &&
      portfolio.holdings.some(holding => {
        // If showTaxDetails is true but lots are undefined or empty, that's invalid
        if (isEmpty(holding.lots)) {
          return true;
        }
        // Check if any lot has missing or invalid values
        return holding.lots!.some(lot => isNil(lot.costBasis) || lot.costBasis === 0 || isNil(lot.units) || lot.units === 0);
      });

    // If an item has Overridden Performances, we let the user save to a comparison as is
    const overrideHoldingsException = !!comparisonItem?.overrideHoldings;

    // Combine into logical groups for readability
    const hasInvalidPortfolioData = isNameMissing || isSizeInvalid || portfolio.holdings.length <= 0;
    const hasInvalidHoldingsData = isHoldingsMissingDollars || (!overrideHoldingsException && isMissingHoldings);
    const hasAllocationIssues =
      (!allowUnallocated && !overrideHoldingsException && isNotFullyAllocated) ||
      (!!allowUnallocated && !allocationWithinRange) ||
      (!!isPortfolioValueLocked && isNotFullyAllocated);
    const hasCostBasisIssues = shouldDisableForCostBasis || hasInvalidTaxLots;

    const saveIsDisabled =
      isCalculating || isSaving || !isDirty || hasInvalidPortfolioData || hasInvalidHoldingsData || hasAllocationIssues || hasCostBasisIssues;

    return saveIsDisabled && !isComparisonReran;
  };

  const hideClearHoldingsModal = () => {
    setShowClearHoldingsModal(false);
  };

  const onDiscardMultiPortfolio = () => {
    clearHoldings();

    if (hasMorePortfolios) {
      processNextPortfolio();
    } else {
      // Show success notification only if at least one portfolio was saved
      if (isInMultiPortfolioMode && savedPortfolioCount > 0) {
        // we stil need to show the success message if we discard on the last portfolio after saving some
        const successMessage =
          savedPortfolioCount > 1 ? formatMessage('HoldingsEditor.Saved.MULTI_PORTFOLIOS') : formatMessage('HoldingsEditor.Saved.PORTFOLIO');
        notification.success(successMessage);
      }

      resetMultiPortfolio();
      clearHoldings();
      onCancel(portfolio);
    }
  };

  return (
    <div className="portfolio-editor-footer-container">
      <Modal.Footer className="portfolio-editor-footer">
        {isMultiPortfoliosEnabled ? (
          <MultiPortfoliosEditorFooterActions isSaving={isSaving} isSaveDisabled={isSaveDisabled()} onDiscard={onDiscardMultiPortfolio} onSave={onSave} />
        ) : (
          <PortfolioEditorFooterActions isSaving={isSaving} isSaveDisabled={isSaveDisabled()} onSave={onSave} />
        )}
      </Modal.Footer>

      <ClearHoldingsModal show={showClearHoldingsModal} onClose={hideClearHoldingsModal} />
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => {
  const { editPortfolio, aiStatementReaderMultiPortfolio } = state;

  return {
    comparisonItem: editPortfolio.comparisonItem,
    isCalculating: editPortfolio.isCalculating,
    isDirty: editPortfolio.isDirty,
    isInMultiPortfolioMode: getIsInMultiPortfolioMode(aiStatementReaderMultiPortfolio),
    isPortfolioValueLocked: editPortfolio.isPortfolioValueLocked,
    isSaving: editPortfolio.isSaving,
    missingHoldings: getPortfolioMissingOrInvalidHoldings(selectShelfStatusedPortfolioHoldings(editPortfolio.portfolio)),
    portfolio: editPortfolio.portfolio,
    showTaxDetails: editPortfolio.showTaxDetails,
    hasMorePortfolios: hasNextPortfolio(aiStatementReaderMultiPortfolio),
    user: state.auth.user || null,
    savedPortfolioCount: aiStatementReaderMultiPortfolio.savedPortfolioIds?.length || 0,
  };
};

export default connect(mapStateToProps, {
  clearHoldings: clearHoldingsAction,
  processNextPortfolio: processNextPortfolioAction,
  resetMultiPortfolio: resetMultiPortfolioAction,
  setHoldingToReplace,
})(withErrorBoundary(PortfolioEditorFooter));
